import { Box, Divider, Grid, Stack } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import useGlobalStyles from 'styles/globalStyles';
import { useMemo } from 'react';
import CurrentPlanDetailsSkeleton from 'components/skeletons/CurrentPlanDetailsSkeleton';
import PlanIcon from 'components/common/PlanIcon/PlanIcon';

const FONT_SIZE = 18;

interface Props {
  plan: any;
}

export default function CurrentPlanDetails({ plan: currentPlan }: Props) {
  const globalStyles = useGlobalStyles();

  const casesPerMonthLimit = useMemo(() => {
    const limit = currentPlan?.data?.features?.cases_per_month_limit;
    return limit > 10000 ? 'Unlimited cases per month' : `${limit} cases per month`;
  }, [currentPlan]);

  const patientsImagesLimit = useMemo(() => {
    const limit = currentPlan?.data?.features?.upload_image;
    return limit ? 'Unlimited patients images' : 'No patients images';
  }, [currentPlan]);

  const registriesLimit = useMemo(() => {
    const limit = currentPlan?.data?.features?.registries_limit;
    return limit == 0 ? 'No registries'
      : limit == 1 ? 'Up to 1 registry'
        : limit > 10000 ? 'Unlimited registries'
          : `Up to ${limit} registries`
  }, [currentPlan]);

  const usedCases = useMemo(() => {
    const limit = currentPlan?.data?.features?.used_cases;
    return limit == 0 ? '0 cases added'
      : limit == 1 ? '1 case added'
        : `${limit} cases added`;
  }, [currentPlan]);

  const usedImages = useMemo(() => {
    const limit = currentPlan?.data?.features?.uploaded_images;
    return limit == 0 ? '0 images added'
      : limit == 1 ? '1 patient image added'
        : `${limit} patients images added`;
  }, [currentPlan]);

  const usedRegistries = useMemo(() => {
    const limit = currentPlan?.data?.features?.used_registries;
    return limit == 0 ? '0 registries created'
      : limit == 1 ? '1 registry created'
        : `${limit} registries created`;
  }, [currentPlan]);


  if (!currentPlan) {
    return <CurrentPlanDetailsSkeleton />
  }

  return (
    <Box className={globalStyles.container}>

      {/* Header */}
      <Box mt={3}>
        <Box className={globalStyles.sectionContainer}>
          <Box
            className={globalStyles.sectionHeader}
            sx={{ position: 'relative' }}
          >
            <Stack flex={1} py={1.5}>

              {/* Plan Details */}
              <Box
                minWidth={150}>
                <CustomText
                  text={currentPlan?.data?.plan?.name}
                  fontSize={22}
                  fontWeight={600}
                />
                <CustomText
                  text={`$${currentPlan?.data?.cost} / ${currentPlan?.data?.period}`}
                  fontSize={18}
                />
              </Box>

            </Stack>

            <Box sx={{
              position: 'absolute',
              right: 30,
              top: 0,
              height: 30,
            }}>
              <PlanIcon planName={currentPlan.data.plan.name} />
            </Box>
          </Box>
        </Box>
      </Box >


      {/* Content */}
      < Grid
        container
        spacing={{
          sm: 0,
          lg: 1.5
        }
        }
      >
        {/* Left section */}
        < Grid
          mt={3}
          className={globalStyles.sectionContainer}
          size={{
            sm: 12,
            lg: 6
          }}
        >
          <Box className={globalStyles.sectionHeader}>
            <CustomText text={'Plan Features'} fontWeight={600} />
          </Box>

          <Divider />

          <Stack px={2} pt={1} pb={1.5} spacing={1}>
            <CustomText text={casesPerMonthLimit} color='black' fontSize={FONT_SIZE} />
            <Divider />
            <CustomText text={patientsImagesLimit} color='black' fontSize={FONT_SIZE} />
            <Divider />
            <CustomText text={registriesLimit} color='black' fontSize={FONT_SIZE} />
            <Divider />
            <CustomText text={`Automated RVUs/Reimbursement estimation`} color='black' fontSize={FONT_SIZE} />
          </Stack>
        </Grid >

        {/* Right section */}
        < Grid
          mt={3}
          className={globalStyles.sectionContainer}
          size={{
            sm: 12,
            lg: 6
          }}
        >
          <Box className={globalStyles.sectionHeader}>
            <CustomText text={'Used Features'} fontWeight={600} />
          </Box>

          <Divider />

          <Stack px={2} pt={1} pb={1.5} spacing={1}>
            <CustomText text={usedCases} color='black' fontSize={FONT_SIZE} />
            <Divider />
            <CustomText text={usedImages} color='black' fontSize={FONT_SIZE} />
            <Divider />
            <CustomText text={usedRegistries} color='black' fontSize={FONT_SIZE} />
            <Divider />
            <CustomText text={`Automated RVUs/Reimbursement estimation`} color='black' fontSize={FONT_SIZE} />
          </Stack>
        </Grid >
      </Grid >
    </Box >
  );
}